import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar, Button, ButtonBase, CssBaseline, Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText,
  ListSubheader, Menu, MenuItem, Toolbar, useMediaQuery, useTheme,
} from '@material-ui/core';
import {
  AccountTree, Close, BarChart, Event as EventIcon, ExpandMore, Gavel, GetApp, Home, Map as MapIcon, Menu as MenuIcon, People, QuestionAnswer, SportsEsports, SportsKabaddi, SportsMma, Stars,
} from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import FortIcon from 'components/Icons/FortIcon';
import { downloadApp } from 'utilities/download';

const ACCENT = '#42a5f5';

interface NavLinkItem {
  label: string;
  path: string;
  icon: React.ReactElement;
}

interface NavGroup {
  label: string;
  items: NavLinkItem[];
}

type NavEntry = NavLinkItem | NavGroup;

const NAV: NavEntry[] = [
  {
    label: 'Leaderboards',
    items: [
      {label: 'Ranked', path: '/leaderboard', icon: <BarChart />},
      {label: '1v1 Duels', path: '/duelleaderboard', icon: <SportsMma />},
      {label: 'Dungeons & Raids', path: '/pve/leaderboard', icon: <FortIcon />},
    ],
  },
  {
    label: 'Match History',
    items: [
      {label: 'Past Games', path: '/game', icon: <SportsEsports />},
      {label: 'Past Duels', path: '/duel', icon: <SportsKabaddi />},
      {label: 'Dungeon & Raid Runs', path: '/pve/runs', icon: <FortIcon />},
    ],
  },
  {label: 'Tournaments', path: '/tournaments', icon: <AccountTree />},
  {label: 'Events', path: '/events', icon: <EventIcon />},
  {label: 'Characters', path: '/characters', icon: <People />},
  {label: 'Feat Planner', path: '/feats', icon: <Stars />},
  {label: 'PvP Maps', path: '/maps', icon: <MapIcon />},
  {label: 'Rules', path: '/rules', icon: <Gavel />},
  {label: 'FAQ', path: '/faq', icon: <QuestionAnswer />},
];

const isGroup = (entry: NavEntry): entry is NavGroup => (entry as NavGroup).items != null;

/** "/game" is active on "/game" and "/game/tdm/123"; "/" only on the home page. */
const isActive = (pathname: string, path: string) => path == '/' ? pathname == '/' : pathname == path || pathname.startsWith(path + '/');

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  appBar: {
    background: 'rgba(17, 18, 22, 0.88)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.35)',
  },
  toolbar: {
    width: '100%',
    maxWidth: 1400,
    margin: '0 auto',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    marginLeft: theme.spacing(-1),
    borderRadius: 8,
  },
  logo: {
    width: 34,
    height: 34,
    marginRight: theme.spacing(1.25),
    filter: 'drop-shadow(0 2px 6px rgba(66, 165, 245, 0.35))',
  },
  brandName: {
    fontSize: '1.15rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
  brandAccent: {
    color: ACCENT,
  },
  links: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    marginLeft: theme.spacing(4),
  },
  link: {
    position: 'relative',
    padding: theme.spacing(0, 1.75),
    fontSize: '0.92rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'color 0.15s',
    '&:hover': {
      color: '#fff',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 14,
      right: 14,
      bottom: 0,
      height: 3,
      borderRadius: '3px 3px 0 0',
      background: ACCENT,
      transform: 'scaleX(0)',
      transition: 'transform 0.2s',
    },
  },
  linkActive: {
    color: '#fff',
    '&::after': {
      transform: 'scaleX(1)',
    },
  },
  chevron: {
    fontSize: '1.1rem',
    marginLeft: 2,
    opacity: 0.7,
  },
  grow: {
    flexGrow: 1,
  },
  download: {
    fontWeight: 700,
    borderRadius: 8,
    textTransform: 'none',
    padding: theme.spacing(0.75, 2),
    background: `linear-gradient(135deg, #1e88e5, #1565c0)`,
    boxShadow: '0 4px 14px rgba(30, 136, 229, 0.4)',
    '&:hover': {
      background: `linear-gradient(135deg, #2196f3, #1976d2)`,
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(255,255,255,0.7)',
    '& svg': {
      fontSize: '1.2rem',
    },
  },
  drawerPaper: {
    width: 290,
    background: '#17181d',
    borderRight: '1px solid rgba(255,255,255,0.08)',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 1.5, 1.5, 2),
  },
  subheader: {
    background: 'transparent',
    lineHeight: '32px',
    marginTop: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
  },
  drawerItem: {
    margin: theme.spacing(0.25, 1),
    width: 'auto',
    borderRadius: 8,
  },
  drawerItemActive: {
    background: 'rgba(66, 165, 245, 0.14)',
    boxShadow: `inset 3px 0 0 ${ACCENT}`,
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: '#bbdefb',
      fontWeight: 600,
    },
  },
  drawerFooter: {
    marginTop: 'auto',
    padding: theme.spacing(2),
  },
  spacer: {
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
  },
}));

/** The site's top bar: brand, page links (grouped into dropdowns), and the app download. A drawer on small screens. */
export const Layout: React.FC = ({children}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const compact = useMediaQuery(theme.breakpoints.down('sm'), {noSsr: true});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menu, setMenu] = useState<{group: NavGroup, anchor: HTMLElement} | null>(null);

  const go = (path: string) => {
    history.push(path);
    setMenu(null);
    setDrawerOpen(false);
  };

  const brand = (
    <ButtonBase className={classes.brand} onClick={() => go('/')} aria-label="PvP Levelup home">
      <img className={classes.logo} src="/icon.png" alt="" />
      <span className={classes.brandName}>PvP <span className={classes.brandAccent}>Levelup</span></span>
    </ButtonBase>
  );

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" elevation={0} className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          {compact &&
            <IconButton color="inherit" edge="start" aria-label="Open menu" onClick={() => setDrawerOpen(true)} style={{marginRight: 8}}>
              <MenuIcon />
            </IconButton>
          }
          {brand}
          {!compact &&
            <nav className={classes.links}>
              {NAV.map((entry) => {
                const active = isGroup(entry) ? entry.items.some((item) => isActive(location.pathname, item.path)) : isActive(location.pathname, entry.path);
                return (
                  <ButtonBase key={entry.label} className={clsx(classes.link, active && classes.linkActive)}
                    onClick={(event) => isGroup(entry) ? setMenu({group: entry, anchor: event.currentTarget}) : go(entry.path)}
                    aria-haspopup={isGroup(entry) ? 'true' : undefined}>
                    {entry.label}
                    {isGroup(entry) && <ExpandMore className={classes.chevron} />}
                  </ButtonBase>
                );
              })}
            </nav>
          }
          <div className={classes.grow} />
          {compact
            ? <IconButton color="inherit" edge="end" aria-label="Download the app" onClick={downloadApp}><GetApp /></IconButton>
            : <Button variant="contained" color="primary" startIcon={<GetApp />} className={classes.download} onClick={downloadApp}>Download App</Button>
          }
        </Toolbar>
      </AppBar>

      <Menu open={menu != null} anchorEl={menu?.anchor} onClose={() => setMenu(null)} keepMounted>
        {menu?.group.items.map((item) => (
          <MenuItem key={item.path} selected={isActive(location.pathname, item.path)} onClick={() => go(item.path)} style={{minWidth: 230}}>
            <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <Drawer variant="temporary" anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)} classes={{paper: classes.drawerPaper}}>
        <div className={classes.drawerHeader}>
          {brand}
          <IconButton aria-label="Close menu" onClick={() => setDrawerOpen(false)}><Close /></IconButton>
        </div>
        <Divider />
        <List dense={false}>
          <ListItem button className={clsx(classes.drawerItem, isActive(location.pathname, '/') && classes.drawerItemActive)} onClick={() => go('/')}>
            <ListItemIcon className={classes.menuIcon}><Home /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItem>
          {NAV.map((entry) => isGroup(entry)
            ? <React.Fragment key={entry.label}>
                <ListSubheader className={classes.subheader} disableSticky>{entry.label}</ListSubheader>
                {entry.items.map((item) => (
                  <ListItem key={item.path} button className={clsx(classes.drawerItem, isActive(location.pathname, item.path) && classes.drawerItemActive)} onClick={() => go(item.path)}>
                    <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItem>
                ))}
              </React.Fragment>
            : null
          )}
          <ListSubheader className={classes.subheader} disableSticky>More</ListSubheader>
          {NAV.filter((entry) => !isGroup(entry)).map((entry) => {
            const item = entry as NavLinkItem;
            return (
              <ListItem key={item.path} button className={clsx(classes.drawerItem, isActive(location.pathname, item.path) && classes.drawerItemActive)} onClick={() => go(item.path)}>
                <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            );
          })}
        </List>
        <div className={classes.drawerFooter}>
          <Button fullWidth variant="contained" color="primary" startIcon={<GetApp />} className={classes.download} onClick={downloadApp}>Download App</Button>
        </div>
      </Drawer>

      <main className={classes.content}>
        <div className={classes.spacer} />
        {children}
      </main>
    </div>
  );
}
